import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import PhotoAlbum from "react-photo-album";
import "react-photo-album/styles.css";
import Lightbox from "yet-another-react-lightbox";
import Download from "yet-another-react-lightbox/plugins/download";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import styles from "./Apt300Gallery.module.css";

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const GALLERY_ASSET_BASE_URL = (
  process.env.REACT_APP_GALLERY_ASSET_BASE_URL ||
  "https://storage.googleapis.com/apt300-gallery"
).replace(/\/$/, "");
const LOCAL_GALLERY_PREFIX = "/apt300-gallery";
const LEGACY_HIDDEN_EVENT_KEY = "december-2025";

const resolveGalleryAssetUrl = (assetPath) => {
  if (!assetPath || /^(https?:)?\/\//i.test(assetPath)) return assetPath;

  const relativePath = assetPath.startsWith(LOCAL_GALLERY_PREFIX)
    ? assetPath.slice(LOCAL_GALLERY_PREFIX.length)
    : assetPath;

  return `${GALLERY_ASSET_BASE_URL}${relativePath.startsWith("/") ? relativePath : `/${relativePath}`}`;
};

const sanitizePhotoId = (value = "") => {
  const parts = value.split("/");
  const filename = parts[parts.length - 1] || "";
  return filename.replace(/\.[^.]+$/, "").toLowerCase();
};

const getPhotoId = (photo) => {
  if (!photo) return "";
  return sanitizePhotoId(photo.fullSrc || photo.src || "");
};

const getPhotoIds = (photo) => {
  const photoId = getPhotoId(photo);
  if (!photoId || photo.event !== LEGACY_HIDDEN_EVENT_KEY) return [photoId];

  const separatorIndex = photoId.lastIndexOf("--");
  if (separatorIndex < 0) return [photoId];

  return [photoId, photoId.slice(separatorIndex + 2)];
};

function Apt300Gallery() {
  const [photos, setPhotos] = useState([]);
  const [index, setIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(true);
  const showAdminControls = new URLSearchParams(window.location.search).has("guest");

  // Load photos + hidden-list
  useEffect(() => {
    async function loadPhotos() {
      try {
        const galleryResponse = await fetch(
          `${GALLERY_ASSET_BASE_URL}/gallery.json?refresh=${Date.now()}`,
          { cache: "no-store" }
        );
        const galleryData = (await galleryResponse.json()).map((photo) => ({
          ...photo,
          src: resolveGalleryAssetUrl(photo.src),
          fullSrc: resolveGalleryAssetUrl(photo.fullSrc),
        }));

        const hiddenUrl = `${API_BASE_URL}/api/gallery/hidden`;
        let hiddenPhotoIds = [];

        try {
          const hiddenResponse = await fetch(hiddenUrl, {
            headers: { "Content-Type": "application/json" },
          });
          if (hiddenResponse.ok) {
            const hiddenJson = await hiddenResponse.json();
            hiddenPhotoIds = hiddenJson?.hiddenPhotoIds || [];
          }
        } catch (hiddenError) {
          console.warn(
            "Gallery hidden API unavailable, falling back to static list",
            hiddenError
          );
        }

        if (hiddenPhotoIds.length) {
          const normalizedHidden = hiddenPhotoIds.map(
            (id) => id?.toLowerCase?.() || ""
          );
          const visiblePhotos = galleryData.filter(
            (photo) =>
              !getPhotoIds(photo).some((photoId) =>
                normalizedHidden.includes(photoId)
              )
          );
          setPhotos(visiblePhotos);
        } else {
          setPhotos(galleryData);
        }
      } catch (err) {
        console.error("Failed to load gallery.json", err);
      } finally {
        setIsLoading(false);
      }
    }

    loadPhotos();
  }, []);

  const hidePhotoByIndex = useCallback(
    async (photoIndex) => {
      const targetPhoto = photos[photoIndex];
      const photoId = getPhotoId(targetPhoto);

      if (!photoId) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/gallery/${encodeURIComponent(photoId)}/hide`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to hide photo: ${response.status}`);
        }

        setPhotos((prev) => prev.filter((_, idx) => idx !== photoIndex));
        setIndex((prevIndex) => {
          if (prevIndex === photoIndex) return -1;
          if (prevIndex > photoIndex) return prevIndex - 1;
          return prevIndex;
        });
      } catch (error) {
        console.error("Failed to hide photo", error);
      }
    },
    [photos]
  );

  const handleHideClick = useCallback(
    async (event, photoIndex) => {
      event.preventDefault();
      event.stopPropagation();
      await hidePhotoByIndex(photoIndex);
    },
    [hidePhotoByIndex]
  );

  const handleLightboxHide = useCallback(async () => {
    if (index < 0) return;
    await hidePhotoByIndex(index);
  }, [index, hidePhotoByIndex]);

  const eventGroups = useMemo(() => {
    const groups = new Map();

    photos.forEach((photo) => {
      const key = photo.event || "apt300-gallery";
      const current = groups.get(key) || {
        key,
        label: photo.eventLabel || "APT 300",
        city: photo.eventCity || "",
        dressCode: photo.eventDressCode || "",
        date: photo.eventDate || "",
        photos: [],
      };
      current.photos.push(photo);
      groups.set(key, current);
    });

    return Array.from(groups.values());
  }, [photos]);

  if (!photos.length && !isLoading) {
    return null;
  }

  const columnBreakpoints = [480, 760, 1024, 1440];

  const columns = (containerWidth) => {
    if (!containerWidth) return 2;
    if (containerWidth < 520) return 2;
    if (containerWidth < 760) return 3;
    if (containerWidth < 1024) return 4;
    return 5;
  };

  const spacing = (containerWidth) =>
    containerWidth && containerWidth < 600 ? 6 : 12;
  const padding = (containerWidth) =>
    containerWidth && containerWidth < 760 ? 3 : 6;

  const sizes = {
    size: "90vw",
    sizes: [
      { viewport: "(max-width: 768px)", size: "calc(100vw - 32px)" },
      { viewport: "(min-width: 1200px)", size: "1100px" },
    ],
  };

  const renderPhotoButton = (
    { ref, children, style, className, onClick },
    { photo }
  ) => {
    const photoIndex = photos.indexOf(photo);

    return (
      <div ref={ref} className={className} style={style}>
        <button
          type="button"
          className={styles.photoButton}
          onClick={onClick}
          aria-label={photo?.label || "Open APT 300 photo"}
        >
          {children}
        </button>
        {showAdminControls && (
          <button
            type="button"
            className={styles.hideButton}
            onClick={(event) => handleHideClick(event, photoIndex)}
          >
            Hide photo
          </button>
        )}
      </div>
    );
  };

  return (
    <section id="gallery" className={styles.gallerySection}>
      <h2 className={styles.heading}>APT 300 PHOTO GALLERY</h2>

      <div className={styles.inner}>
        {isLoading ? (
          <div
            id="loading"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "50vh",
              width: "100%",
            }}
          >
            <img
              src="/apt300-loading.GIF"
              alt="Loading..."
              style={{ maxWidth: "100px", width: "100%" }}
            />
          </div>
        ) : (
          <div className={styles.albumWrapper}>
            {eventGroups.map((group) => {
              return (
                <section
                  key={group.key}
                  id={`gallery-event-${group.key}`}
                  className={styles.eventSection}
                  aria-labelledby={`gallery-event-${group.key}-heading`}
                >
                  <h3
                    id={`gallery-event-${group.key}-heading`}
                    className={styles.eventHeading}
                  >
                    {group.label}
                  </h3>
                  <div className={styles.eventMeta}>
                    {group.city && <span>{group.city}</span>}
                    {group.date && <span>{group.date}</span>}
                    {group.dressCode && (
                      <span>Dress code: {group.dressCode}</span>
                    )}
                  </div>
                  <PhotoAlbum
                    layout="columns"
                    breakpoints={columnBreakpoints}
                    photos={group.photos}
                    columns={columns}
                    spacing={spacing}
                    padding={padding}
                    sizes={sizes}
                    render={{ button: renderPhotoButton }}
                    onClick={({ photo: clickedPhoto }) =>
                      setIndex(photos.indexOf(clickedPhoto))
                    }
                  />
                </section>
              );
            })}
          </div>
        )}
      </div>

      <Lightbox
        open={index >= 0}
        close={() => setIndex(-1)}
        index={index}
        slides={photos.map((photo) => ({
          src: photo.src,
          download: photo.fullSrc || photo.src,
          downloadFilename: photo.src?.split("/").pop(),
        }))}
        plugins={[Download, Zoom]}
        controller={{ closeOnPullDown: true }}
        toolbar={{
          buttons: [
            "download",
            ...(showAdminControls && index >= 0
              ? [
                  <button
                    key="hide-photo-toolbar"
                    type="button"
                    className={styles.hideToolbarButton}
                    onClick={handleLightboxHide}
                  >
                    Hide photo
                  </button>,
                ]
              : []),
            "close",
          ],
        }}
      />
    </section>
  );
}

export default Apt300Gallery;
